import { Tooltip } from 'antd';
import useDeclarations from 'hooks/useDeclarations';
import useJobs from 'hooks/useJobs';
import { ReactElement, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import useFormUtils from '../../../hooks/useFormUtils';
import { gvmsNameLabels } from '../../../store/declarations/uk/gvms-declaration';
import UkGvmsRecordUtils from '../uk/gvms/utils';
import Box44 from './box44/Box44';
import useOpenBox44OnRequest from './box44/useOpenBox44OnRequest';
import { checkForItemsCap, getJobPath, getJobReferenceLinks, getMrn } from '../utils/declaration-utils';
import MRNLookupModal from './misc/MRNLookupModal';
import SearchCustomerButton from './search-customer/SearchCustomerButton';
import Button from 'components/new-ui/base/button/Button';
import { SBox44Button } from './box44/components/styles';
import PlusIcon from './box44/components/PlusIcon';
import PopoverButtonsGroup from 'components/new-ui/composed/popover-buttons-group/PopoverButtonsGroup';
import { StyledPopoverButton } from 'components/new-ui/composed/popover-buttons-group/styles';
import BulkUploadItemsModal from './bulk-items-upload/BulkUploadItemsModal';
import { InfoText, InfoTitle, InfoValue, InfoWrapper } from './DeclarationInformation.styles';
import JobReferenceIdLinksList from './job-reference-links-list/JobReferenceIdLinksList';

const Container = styled.div<{
    inProductTemplate?: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 3rem;
    flex-grow: 1;
    align-self: center;
    line-height: normal;
    ${({ inProductTemplate }) => (inProductTemplate ? '' : 'margin-left: 8px;')};
`;

type ActiveModal = 'mrnLookup' | 'bulkUpload' | 'box44';

type VisibleInfo = NonNullable<Props['visibleInfo']>;

const defaultVisibleInfo: VisibleInfo = {
    viewAllProducts: false,
    createProduct: false,
    template: true,
    declaration: true,
    jobReferences: true,
    ref: false,
    status: true,
    gmrId: false,
    record: false,
    pbnId: false,
    mrn: false,
    box44: false,
    toMasterItems: false,
    toHouseConsignments: false,
    addMasterItem: false,
    addHouseConsignment: false,
    addHouseConsignmentProduct: false,
};

interface Props {
    handleViewAllProductsBtn?: () => void;
    handleAddNewProductBtn?: () => Promise<void>;
    handleAddMasterItemBtn?: () => Promise<void>;
    handleAddHouseConsignmentBtn?: () => Promise<void>;
    handleBackToMasterItemsBtn?: () => Promise<void>;
    handleBackToHouseConsignmentsBtn?: () => Promise<void>;
    handleAddHouseConsignmentProductBtn?: () => Promise<void>;
    refetchProducts?: () => Promise<void>;
    viewOnly?: boolean;
    amendment?: boolean;
    productsCount?: number;
    visibleInfo?: {
        viewAllProducts?: boolean;
        createProduct?: boolean;
        bulkUploadItems?: boolean;
        template?: boolean;
        declaration?: boolean;
        jobReferences?: boolean;
        ref?: boolean;
        status?: boolean;
        gmrId?: boolean;
        record?: boolean;
        pbnId?: boolean;
        mrn?: boolean;
        box44?: boolean;
        addHouseConsignment?: boolean;
        addHouseConsignmentProduct?: boolean;
        toHouseConsignments?: boolean;
        toMasterItems?: boolean;
        addMasterItem?: boolean;
    };
}

const DeclarationInformation = ({
    handleViewAllProductsBtn,
    handleAddNewProductBtn,
    handleAddMasterItemBtn,
    handleAddHouseConsignmentBtn,
    handleBackToMasterItemsBtn,
    handleBackToHouseConsignmentsBtn,
    handleAddHouseConsignmentProductBtn,
    refetchProducts,
    viewOnly,
    amendment,
    productsCount,
    visibleInfo,
}: Props): ReactElement => {
    const { declaration } = useDeclarations();
    const { productId, consignmentId } = useParams();
    const location = useLocation();
    const { job } = useJobs({ jobId: declaration?.jobId });
    const navigate = useNavigate();
    const { declarationFormType, country, internalType } = useFormUtils();

    const [productsActionsPopoverOpen, setProductsActionsPopoverOpen] = useState(false);
    const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

    const openModal = (modal: ActiveModal) => setActiveModal(modal);
    const isModalOpen = (modal: ActiveModal) => activeModal === modal;
    const closeModal = () => setActiveModal(null);

    const { isAtItemsCap, itemsCap } = useMemo(
        () => checkForItemsCap(country, internalType, declarationFormType, productsCount),
        [country, internalType, declarationFormType, productsCount]
    );

    const inProductTemplateAdd = useMatch('customs-declarations/:country/:internalType/products/:type/new');
    const inProductTemplateEdit = useMatch('customs-declarations/:country/:internalType/products/:type/:id');
    const inProductTemplate = useMemo(
        () => inProductTemplateAdd || inProductTemplateEdit,
        [inProductTemplateAdd, inProductTemplateEdit]
    );

    const showButton = useMemo(
        () => !location.pathname.includes('products') || productId || inProductTemplate,
        [inProductTemplate, location.pathname, productId]
    );

    const mrn = useMemo(() => declaration && getMrn(declaration), [declaration]);

    const showMRNLookupModal = () => {
        if (!mrn) return;
        openModal('mrnLookup');
    };

    const jobId = job?.referenceId;
    const inManifests = Boolean(job?.manifest?.id && jobId);
    const baseUrl = inManifests
        ? `/manifests/${job?.manifest?.id}/${jobId}/${declaration?.id}`
        : `/declarations/${declaration?.id}`;
    const linkToJob = getJobPath(job?.id, job?.manifest?.id);

    const jobReferencesLinks = useMemo(() => getJobReferenceLinks(declaration, job), [declaration, job]);

    const resolvedVisibleInfo: VisibleInfo = {
        ...defaultVisibleInfo,
        ...visibleInfo,
    };

    useOpenBox44OnRequest(!!resolvedVisibleInfo.box44, () => setActiveModal('box44'));

    return (
        <Container inProductTemplate={!!inProductTemplate}>
            {resolvedVisibleInfo?.jobReferences && <JobReferenceIdLinksList data={jobReferencesLinks} />}
            {resolvedVisibleInfo?.record && (
                <InfoWrapper data-testid="declaration-information-form-type">
                    <InfoTitle>Record: </InfoTitle>
                    <InfoValue>
                        {gvmsNameLabels.find((label) => label.key === declarationFormType)?.value ?? '-'}
                    </InfoValue>
                </InfoWrapper>
            )}
            {/* {options?.visible?.ref && ( */}
            {/*     <InfoWrapper data-testid="declaration-information-ref"> */}
            {/*         <InfoTitle>Ref: </InfoTitle> */}
            {/*         <InfoText>{ref}</InfoText> */}
            {/*     </InfoWrapper> */}
            {/* )} */}
            {resolvedVisibleInfo?.gmrId && (
                <InfoWrapper data-testid="declaration-information-gmr-id">
                    <InfoWrapper>
                        <InfoTitle>GMR ID: </InfoTitle>
                        <InfoValue>{UkGvmsRecordUtils.addSpacesToGmrId(declaration?.gvmsDeclaration?.gmrId)}</InfoValue>
                    </InfoWrapper>
                </InfoWrapper>
            )}
            {resolvedVisibleInfo?.pbnId && (
                <InfoWrapper data-testid="declaration-information-pbn-id">
                    <InfoWrapper>
                        <InfoTitle>PBN ID: </InfoTitle>
                        <InfoValue>{declaration?.preBoardingNotification?.pbnId}</InfoValue>
                    </InfoWrapper>
                </InfoWrapper>
            )}
            {resolvedVisibleInfo?.mrn && (
                <InfoWrapper data-testid="declaration-information-mrn">
                    <InfoTitle>MRN: </InfoTitle>
                    <Tooltip overlay={mrn ?? 'N/A'}>
                        <InfoText onClick={showMRNLookupModal}>{mrn ?? 'N/A'}</InfoText>
                    </Tooltip>
                    {mrn ? (
                        <SearchCustomerButton title="MRN Lookup" compact condensed onClick={showMRNLookupModal} />
                    ) : null}
                </InfoWrapper>
            )}
            {/* LRN and Ref number to be hidden for now according to Dave & Bozhana */}
            {/* {lrn && ( */}
            {/*     <InfoWrapper data-testid="declaration-information-lrn"> */}
            {/*         <InfoTitle>LRN: </InfoTitle> */}
            {/*         <Tooltip overlay={lrn}> */}
            {/*             <InfoText>{lrn}</InfoText> */}
            {/*         </Tooltip> */}
            {/*     </InfoWrapper> */}
            {/* )} */}
            <div style={{ display: 'flex', marginLeft: 'auto', gap: '10px' }}>
                {handleAddNewProductBtn && resolvedVisibleInfo?.createProduct && !viewOnly && (
                    <InfoWrapper>
                        <PopoverButtonsGroup
                            open={productsActionsPopoverOpen}
                            onOpenChange={setProductsActionsPopoverOpen}
                            buttonEl={
                                <Tooltip
                                    title={
                                        isAtItemsCap ? `Maximum ${itemsCap} items per declaration reached` : undefined
                                    }
                                >
                                    <span>
                                        <SBox44Button
                                            size="small"
                                            onClick={() => setProductsActionsPopoverOpen(true)}
                                            data-testid={'add-new-product-button'}
                                            disabled={isAtItemsCap}
                                        >
                                            <PlusIcon /> Add product
                                        </SBox44Button>
                                    </span>
                                </Tooltip>
                            }
                        >
                            <StyledPopoverButton
                                onClick={() => {
                                    handleAddNewProductBtn?.();
                                    setProductsActionsPopoverOpen(false);
                                }}
                                data-testid="add-new-product-button"
                            >
                                Create New
                            </StyledPopoverButton>

                            {handleViewAllProductsBtn && (
                                <StyledPopoverButton
                                    onClick={() => {
                                        handleViewAllProductsBtn?.();
                                        setProductsActionsPopoverOpen(false);
                                    }}
                                    data-testid="view-all-product-templates-button"
                                >
                                    Add From Templates
                                </StyledPopoverButton>
                            )}

                            {resolvedVisibleInfo?.bulkUploadItems && (
                                <StyledPopoverButton
                                    disabled={isAtItemsCap}
                                    onClick={() => {
                                        setActiveModal('bulkUpload');
                                        setProductsActionsPopoverOpen(false);
                                    }}
                                    data-testid="bulk-upload-items-button"
                                >
                                    Bulk Upload
                                </StyledPopoverButton>
                            )}
                        </PopoverButtonsGroup>
                    </InfoWrapper>
                )}

                {resolvedVisibleInfo.toMasterItems && (
                    <InfoWrapper>
                        <Button
                            type="link"
                            style={{ color: 'var(--brand-6)' }}
                            onClick={() => handleBackToMasterItemsBtn?.()}
                            data-testid={'back-to-master-items-list-button'}
                        >
                            Back to Master Items
                        </Button>
                    </InfoWrapper>
                )}

                {resolvedVisibleInfo.toHouseConsignments && (
                    <InfoWrapper>
                        <Button
                            type="link"
                            style={{ color: 'var(--brand-6)' }}
                            onClick={() => handleBackToHouseConsignmentsBtn?.()}
                            data-testid={'back-to-house-consignments-list-button'}
                        >
                            Back to House Consignments
                        </Button>
                    </InfoWrapper>
                )}

                {resolvedVisibleInfo.addMasterItem && !viewOnly && (
                    <InfoWrapper>
                        <SBox44Button
                            size="small"
                            onClick={() => handleAddMasterItemBtn?.()}
                            data-testid={'add-master-item-button'}
                        >
                            <PlusIcon />
                            Add Master Item
                        </SBox44Button>
                    </InfoWrapper>
                )}

                {resolvedVisibleInfo.addHouseConsignment && !viewOnly && (
                    <InfoWrapper>
                        <SBox44Button
                            size="small"
                            onClick={() => handleAddHouseConsignmentBtn?.()}
                            data-testid={'add-house-consignment-button'}
                        >
                            <PlusIcon />
                            Add House Consignment
                        </SBox44Button>
                    </InfoWrapper>
                )}

                {resolvedVisibleInfo?.addHouseConsignmentProduct && !viewOnly && (
                    <InfoWrapper>
                        <SBox44Button
                            size="small"
                            onClick={() => handleAddHouseConsignmentProductBtn?.()}
                            data-testid={'add-new-house-consignment-product-button'}
                        >
                            <PlusIcon /> Add Product
                        </SBox44Button>
                    </InfoWrapper>
                )}

                {productId && viewOnly && !consignmentId && !resolvedVisibleInfo.toMasterItems && (
                    <InfoWrapper>
                        <SBox44Button
                            size="small"
                            onClick={() => {
                                navigate(`${baseUrl}/view-only/products`, {
                                    replace: true,
                                    state: { comingBackFromProductView: true },
                                });
                            }}
                            data-testid={'back-to-products-list-button'}
                        >
                            Back to products list
                        </SBox44Button>
                    </InfoWrapper>
                )}

                {resolvedVisibleInfo?.box44 && (
                    <InfoWrapper>
                        {showButton && <Box44.Button onClick={() => setActiveModal('box44')} />}
                        <Box44.Modal
                            viewOnly={viewOnly && !amendment}
                            open={isModalOpen('box44')}
                            onCancel={closeModal}
                        />
                    </InfoWrapper>
                )}
            </div>

            <MRNLookupModal open={isModalOpen('mrnLookup')} onCancel={closeModal} />

            <BulkUploadItemsModal
                numberOfItemsInDeclaration={productsCount}
                open={isModalOpen('bulkUpload')}
                onClose={closeModal}
                onSuccess={() => refetchProducts?.()}
                onItemCapExceeded={() => navigate(linkToJob)}
            />
        </Container>
    );
};

export default DeclarationInformation;
